import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, FileText, AlertTriangle, Shield, Scale, Clock, Users } from "lucide-react";

export default function Terms() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';

  return (
    <div className={`min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className={`mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <ArrowLeft className={`h-4 w-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
            {t('terms.backToHome')}
          </Button>
          
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('terms.title')}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">
            {t('terms.subtitle')}
          </p>
          
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Clock className="h-4 w-4" />
            {t('terms.lastUpdated')}: {t('terms.updateDate')}
          </div>
        </div>

        <div className="space-y-6">
          {/* Acceptance of Terms */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('terms.acceptance.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('terms.acceptance.content')}
              </p>
            </CardContent>
          </Card>

          {/* Service Description */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                {t('terms.service.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('terms.service.description')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                <li>{t('terms.service.expenseTracking')}</li>
                <li>{t('terms.service.whatsappIntegration')}</li>
                <li>{t('terms.service.financialInsights')}</li>
                <li>{t('terms.service.dataStorage')}</li>
              </ul>
            </CardContent>
          </Card>

          {/* User Responsibilities */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('terms.userResponsibilities.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                <li>{t('terms.userResponsibilities.accurateInfo')}</li>
                <li>{t('terms.userResponsibilities.accountSecurity')}</li>
                <li>{t('terms.userResponsibilities.legalUse')}</li>
                <li>{t('terms.userResponsibilities.dataBackup')}</li>
                <li>{t('terms.userResponsibilities.compliance')}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Privacy and Data Protection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                {t('terms.privacy.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('terms.privacy.description')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                <li>{t('terms.privacy.dataEncryption')}</li>
                <li>{t('terms.privacy.limitedAccess')}</li>
                <li>{t('terms.privacy.noThirdParty')}</li>
                <li>{t('terms.privacy.userControl')}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Limitations and Disclaimers */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                {t('terms.limitations.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('terms.limitations.description')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                <li>{t('terms.limitations.noFinancialAdvice')}</li>
                <li>{t('terms.limitations.serviceAvailability')}</li>
                <li>{t('terms.limitations.dataAccuracy')}</li>
                <li>{t('terms.limitations.thirdPartyServices')}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Intellectual Property */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                {t('terms.intellectualProperty.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                {t('terms.intellectualProperty.description')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                <li>{t('terms.intellectualProperty.trademarks')}</li>
                <li>{t('terms.intellectualProperty.software')}</li>
                <li>{t('terms.intellectualProperty.content')}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Prohibited Uses */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                {t('terms.prohibitedUses.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400">
                <li>{t('terms.prohibitedUses.illegalActivities')}</li>
                <li>{t('terms.prohibitedUses.systemInterference')}</li>
                <li>{t('terms.prohibitedUses.unauthorizedAccess')}</li>
                <li>{t('terms.prohibitedUses.falseInformation')}</li>
                <li>{t('terms.prohibitedUses.commercialUse')}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Service Modifications */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.modifications.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('terms.modifications.description')}
              </p>
            </CardContent>
          </Card>

          {/* Termination */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.termination.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('terms.termination.description')}
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-400">
                <li>{t('terms.termination.userInitiated')}</li>
                <li>{t('terms.termination.serviceInitiated')}</li>
                <li>{t('terms.termination.dataRetention')}</li>
              </ul>
            </CardContent>
          </Card>

          {/* Liability and Indemnification */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                {t('terms.liability.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('terms.liability.description')}
              </p>
            </CardContent>
          </Card>

          {/* Governing Law */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.governingLaw.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('terms.governingLaw.description')}
              </p>
            </CardContent>
          </Card>

          {/* Changes to Terms */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.changes.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('terms.changes.description')}
              </p>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t('terms.contact.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                {t('terms.contact.description')}
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <p className="font-semibold">FINGENT</p>
                <p className="text-gray-600 dark:text-gray-400">fingentlife@gmail.com</p>
                <p className="text-gray-600 dark:text-gray-400">{t('terms.contact.location')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 